import { useState } from "react";

import { DateGrid } from "Components/atoms/DateGrid";
import { DatePickerActions } from "Components/atoms/DatePickerActions/Desktop";
import { DatePickerTop } from "Components/atoms/DatePickerTop";
import { useCalendarContext } from "shared/contexts/CalendarContext";
import { useSearchContext } from "shared/contexts/SearchContext";
import { generateDaysInMonth } from "shared/utils/generateDaysInMonth";
import { getFormattedDateDetails } from "shared/utils/getFormattedDateDetails";
import { DatePickerProps } from "../types";
import {
  CalendarMonth,
  CalendarInstruction,
  Calendars,
  DatePickerArea,
  DatePickerHeader,
  SelectionModeButton,
  SelectionModeTabs,
} from "./styles";

export const DatePicker: React.FC<DatePickerProps> = ({
  closePicker,
  selectedDates = [],
  onDatesChange,
  onConfirm,
  onClear,
  maxSelectableDates,
  type,
}) => {
  const {
    currentDate,
    selectionMode,
    setSelectionMode,
    setCurrentDate,
    selectedOriginDates,
    selectedDestinationDates,
    setSelectedOriginDates,
    setSelectedDestinationDates,
  } = useCalendarContext();
  const { setDepartureDates, setReturnDates } = useSearchContext();
  const [departure, setDeparture] = useState<string | null>(null);

  const { searchState } = useSearchContext();

  const selectDate = (date: string) => {
    if (searchState.tripType === "oneWay") {
      setDeparture(null);
      setSelectedOriginDates([date]);
      setSelectedDestinationDates([]);
      setDepartureDates([date]);
      setReturnDates([]);
      return;
    }

    if (!departure) {
      setDeparture(date);
      setSelectedOriginDates([date]);
      setSelectedDestinationDates([]);
      setDepartureDates([date]);
      setReturnDates([]);
      return;
    }

    const [start, end] =
      departure <= date ? [departure, date] : [date, departure];
    setSelectedOriginDates([start]);
    setSelectedDestinationDates([end]);
    setDepartureDates([start]);
    setReturnDates([end]);
    setDeparture(null);
  };

  const changeMonth = (offset: number) =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1),
    );

  const today = new Date();
  const disablePrevious =
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();
  const start = selectedOriginDates[0];
  const end = selectedDestinationDates[0];
  const visibleSelectedDates =
    selectionMode === "single"
      ? [...selectedOriginDates, ...selectedDestinationDates]
      : selectedDates;

  const selectMultipleDate = (date: string) => {
    const updatedDates = selectedDates.includes(date)
      ? selectedDates.filter((selectedDate) => selectedDate !== date)
      : selectedDates.length < maxSelectableDates
        ? [...selectedDates, date]
        : selectedDates;
    onDatesChange(updatedDates);
  };

  const grayDates =
    type === "origin" ? selectedDestinationDates : selectedOriginDates;

  const changeSelectionMode = (mode: "single" | "multi") => {
    if (mode === selectionMode) return;
    setDeparture(null);
    setSelectedOriginDates([]);
    setSelectedDestinationDates([]);
    setDepartureDates([]);
    setReturnDates([]);
    setSelectionMode(mode);
  };

  return (
    <DatePickerArea>
      <DatePickerHeader>
        <SelectionModeTabs role="tablist" aria-label="Modo de seleção de datas">
          <SelectionModeButton
            type="button"
            role="tab"
            aria-selected={selectionMode === "single"}
            isActive={selectionMode === "single"}
            onClick={() => changeSelectionMode("single")}
          >
            {searchState.tripType === "oneWay" ? "Só Ida" : "Ida e Volta"}
          </SelectionModeButton>
          <SelectionModeButton
            type="button"
            role="tab"
            aria-selected={selectionMode === "multi"}
            isActive={selectionMode === "multi"}
            onClick={() => changeSelectionMode("multi")}
          >
            Várias datas
          </SelectionModeButton>
        </SelectionModeTabs>
        {selectionMode === "multi" && (
          <CalendarInstruction>
            Selecione até 4 datas de {type === "destination" ? "volta" : "ida"}
          </CalendarInstruction>
        )}
      </DatePickerHeader>
      <Calendars>
        {[0, 1].map((offset) => {
          const monthDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + offset,
            1,
          );
          const formatted = getFormattedDateDetails(monthDate);
          const days = generateDaysInMonth(
            monthDate.getFullYear(),
            monthDate.getMonth(),
            selectedOriginDates,
            selectedDestinationDates,
          );
          return (
            <CalendarMonth
              key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
            >
              <DatePickerTop
                onPreviousMonth={
                  offset === 0 && !disablePrevious
                    ? () => changeMonth(-1)
                    : undefined
                }
                onNextMonth={
                  offset === 1 ? () => changeMonth(1) : () => changeMonth(1)
                }
                month={formatted.monthFull || ""}
                year={formatted.year || ""}
              />
              <DateGrid
                days={days}
                currentDate={monthDate}
                selectedDates={visibleSelectedDates}
                onDatesChange={() => {}}
                onSelectDate={(day) => {
                  const date = new Date(
                    monthDate.getFullYear(),
                    monthDate.getMonth(),
                    day,
                  );
                  const formattedDate = `${date.getFullYear()}-${String(
                    date.getMonth() + 1,
                  ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  if (selectionMode === "single") selectDate(formattedDate);
                  else selectMultipleDate(formattedDate);
                }}
                rangeStart={selectionMode === "single" ? start : undefined}
                rangeEnd={selectionMode === "single" ? end : undefined}
                type={selectionMode === "multi" ? type : undefined}
                grayDates={selectionMode === "multi" ? grayDates : undefined}
                continuousRange={selectionMode === "single"}
              />
            </CalendarMonth>
          );
        })}
      </Calendars>
      {selectionMode === "multi" && (
        <DatePickerActions
          onClear={() => {
            onDatesChange([]);
            onClear();
          }}
          onConfirm={() => {
            onConfirm(selectedDates);
            closePicker();
          }}
        />
      )}
    </DatePickerArea>
  );
};
